// @flow

// Types
import type {
  AccountOperation,
  AccountOperator,
} from '../../types'

import type {
  Accounts,
} from 'hfty-model-accounts'

import type {
  LoginOperator,
  LoginOperation,
  RequestResetPasswordOperator,
  RequestResetPasswordOperation,
} from 'hfty-auth/types'

// Constants
import {
  USER_EMAIL_EXISTS_REGEX,
  USER_USERNAME_EXISTS_REGEX,
  UPDATE_ACCOUNT_OPERATION,
} from 'hfty-accounts/src/constants'

import {
  PROP_USERNAME,
  PROP_EMAIL,
} from 'hfty-model-accounts'

// Libs
import { push } from 'react-router-redux'
import _size from 'lodash/size'
import _find from 'lodash/find'
import _cloneDeep from 'lodash/cloneDeep'
import _reduce from 'lodash/reduce'

// Operator
import composeAccountsOperator from '../operator'
import requestResetPasswordOperator from 'hfty-auth/src/requestResetPasswordOperator'

// Action Creators
import { login } from 'fc-auth/src'

// Controller
import controller from 'hfty-ctrl-accounts'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

// Selectors
import operations$ from 'hfty-accounts/src/selectors/newAccount'
import loginOperation$ from 'hfty-auth/src/selectors/loginOperation'
import requestResetOperation$ from 'hfty-auth/src/selectors/requestResetPasswordOperation'

// Operators
import loginOperator from 'hfty-auth/src/loginOperator'

import validateOperation from './validateOperation'

const operator = composeAccountsOperator()


// Exports
export default function *watchOperations (): Generator<*, void, *> {
  yield fork(handleOperations)
  yield fork(watchUpdateOperation)
}

export function *watchUpdateOperation (): Generator<*, void, *> {
  while (true) {
    const { payload } = yield take([UPDATE_ACCOUNT_OPERATION])
    yield call(handleUpdateOperation, payload)
  }
}

export function *handleUpdateOperation (operation: FieldBorderOperation): Generator<*, void, *> {
  const validatedOperation = yield call(validateOperation, operation)
  const action: Action<Object> = yield operator.actionCreators.updateOperation(validatedOperation)
  yield put(action)
}

export function *handleOperations (): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })
    if (effect.submit) {
      done = yield call(handleSubmitOperations)
    } else {
      done = true // Cancelled
    }
  }
}

export function *handleSubmitOperations (): Generator<*, boolean, *> {
  let operations = yield select(operations$)

  operations = {
    ...operations,
    submitting: true,
  }

  yield put(operator.actionCreators.updateOperation(operations))

  const nextOperations = yield call(handleSubmitOperation, operations)
  return nextOperations.success
}

// FIXME: when backend removes [user] from error msgs (2) - email & username
// NOTE: ids derived from hfty-accounts/src/messages
export function cleanResponses<E> (msgs: E): E {
  let copy = _cloneDeep(msgs)
  const MapMessages = [
    {
      regEx: USER_EMAIL_EXISTS_REGEX,
      id: 'userWithEmailAlreadyExists',
    },
    {
      regEx: USER_USERNAME_EXISTS_REGEX,
      id: 'userAlreadyExists',
    },
  ]
  const mapKeys = {
    userWithEmailAlreadyExists: PROP_EMAIL,
    userAlreadyExists: PROP_USERNAME,
  }
  return _reduce(copy, function (all: Array, errorObj: Object): E {
    let translate = _find(MapMessages, (map: Object): ?Object => {
      return errorObj.error.match(map.regEx)
    })
    const translation = translate !== undefined ? translate.id : null
    errorObj.key = translate && mapKeys[translate.id]
    errorObj.error = translation || errorObj.error
    all.push(errorObj)
    return all
  }, [])
}

export function *handleSubmitOperation (operation: AccountOperation): Generator<*, AccountOperation, *> {
  const model: Accounts = operation.model

  const validatedOperation = yield call(validateOperation, operation)

  if (_size(validatedOperation.errors)) {
    const nextOperation = {
      ...validatedOperation,
      triedSubmission: true,
    }
    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)
    return nextOperation
  }

  const { error, response } = yield call([controller, controller.create], {
    model,
  })

  if (error) {
    // FIXME NOTE about cleanResponses: fix when backend removes [user] from error msgs (2) - email & username
    let cleanedResponse = cleanResponses(response)

    const nextOperation = {
      ...operation,
      triedSubmission: true,
      submitting: false,
      success: false,
      errors: cleanedResponse,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    // Email already in use flow
    if (cleanedResponse[0].error === 'userWithEmailAlreadyExists') {
      yield call(handleEmailResetPassword, model)
    }

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      triedSubmission: true,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    yield call(handleSubmitOperationSuccess, model)

    return nextOperation
  }
}

export function *handleSubmitOperationSuccess ({ username, password }: Accounts): Generator<*, LoginOperation, *> {
  const operator: LoginOperator = loginOperator()
  const loginOperation: LoginOperation = yield select(loginOperation$)

  const nextOperation = operator.actionCreators.updateOperation({
    ...loginOperation,
    model: {
      username,
      password,
    },
  })
  yield put(nextOperation)
  const loginAction = login()
  yield put(loginAction)

  yield put(push('/getting-started'))

  return loginAction
}

export function *handleEmailResetPassword (model: Accounts): Generator<*, AccountOperation, Accounts> {
  const passwordResetOperator: RequestResetPasswordOperator = requestResetPasswordOperator()
  const emailResetPasswordOperation: RequestResetPasswordOperation = yield select(requestResetOperation$)
  const accountOperator: AccountOperator = operator
  const accountOperation: AccountOperation = yield select(operations$)

  const nextOperation = passwordResetOperator.actionCreators.updateOperation({
    ...emailResetPasswordOperation,
    model: {
      identifier: model.email,
    },
  })
  yield put(nextOperation)

  const resetErrorsOperation = accountOperator.actionCreators.updateOperation({
    ...accountOperation,
    errors: [],
  })
  yield put(resetErrorsOperation)

  return resetErrorsOperation
}
